import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ProductStoreCategory } from "../api/fetchProductStoreCategories/types";
import { CreateProductStoreCategoryRequest } from "../api/createProductStoreCategory/types";

const categorySchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  colorId: z.number().min(1, "Color is required"),
});

export type CategoryFormData = z.infer<typeof categorySchema>;

function getDefaultValues(
  category: ProductStoreCategory | null | undefined,
): CategoryFormData {
  if (!category) {
    return {
      name: "",
      description: "",
      colorId: 0,
    };
  }

  return {
    name: category.name || "",
    description: category.description || "",
    colorId: category.color?.id || 0,
  };
}

interface UseCategoryFormParams {
  initialData?: ProductStoreCategory | null;
  onSubmit: (data: CreateProductStoreCategoryRequest) => Promise<void>;
}

export function useCategoryForm({
  initialData,
  onSubmit,
}: UseCategoryFormParams) {
  const isEditing = !!initialData;

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: getDefaultValues(initialData),
  });

  const { reset, handleSubmit } = form;

  useEffect(() => {
    reset(getDefaultValues(initialData));
  }, [initialData, reset]);

  const onFormSubmit = async (data: CategoryFormData) => {
    await onSubmit({
      name: data.name,
      description: data.description,
      colorId: data.colorId,
    });
  };

  return {
    form,
    isEditing,
    submit: handleSubmit(onFormSubmit),
  };
}
